/**
 * Opportunity Deduplication Utility
 * 
 * Deduplicates opportunities across multiple data sources based on:
 * 1. Exact match: reference_number + source
 * 2. Similar match: title similarity + organization + deadline proximity
 * 3. URL match: same URL (normalized)
 */

import { Opportunity } from '../services/dataSourceIntegrations';

/**
 * Minimal shape the dedup engine needs. `Opportunity` (and the GovtOpportunityEntity
 * proxy built in `deduplicateGovtOpportunityEntities` below) both satisfy this
 * structurally, so the same matching logic can be reused for both call sites instead
 * of being duplicated per entity shape.
 */
export interface DedupeFields {
  id: string | number;
  title: string;
  organization: string;
  deadline: string;
  url?: string;
  reference_number?: string;
  source?: string;
}

export interface DeduplicationResult<T extends DedupeFields = Opportunity> {
  unique: T[];
  duplicates: Array<{
    opportunity: T;
    matchedWith: T;
    matchType: 'exact' | 'similar' | 'url';
    similarity?: number;
  }>;
  stats: {
    total: number;
    unique: number;
    duplicates: number;
    exactMatches: number;
    similarMatches: number;
    urlMatches: number;
  };
}

/**
 * Normalize text for comparison (lowercase, trim, remove special chars)
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ');
}

/**
 * Normalize URL for comparison (remove query params, fragments, trailing slashes)
 */
function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`.toLowerCase().replace(/\/$/, '');
  } catch {
    // If URL parsing fails, just normalize the string
    return url.toLowerCase().trim().replace(/\/$/, '');
  }
}

/**
 * Calculate similarity between two strings using Levenshtein distance
 * Returns a value between 0 and 1 (1 = identical, 0 = completely different)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const s1 = normalizeText(str1);
  const s2 = normalizeText(str2);
  
  if (s1 === s2) return 1.0;
  if (s1.length === 0 || s2.length === 0) return 0.0;
  
  // Use simple word overlap for performance
  const words1 = new Set(s1.split(/\s+/));
  const words2 = new Set(s2.split(/\s+/));
  
  const intersection = new Set([...words1].filter(w => words2.has(w)));
  const union = new Set([...words1, ...words2]);
  
  return intersection.size / union.size;
}

/**
 * Check if two dates are within a reasonable proximity (e.g., same day or within 7 days)
 */
function datesAreProximate(date1: string, date2: string, maxDaysDiff: number = 7): boolean {
  if (!date1 || !date2) return false;
  
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffMs = Math.abs(d1.getTime() - d2.getTime());
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= maxDaysDiff;
  } catch {
    return false;
  }
}

/**
 * Check if two opportunities are exact duplicates
 */
function isExactDuplicate<T extends DedupeFields>(opp1: T, opp2: T): boolean {
  // Exact match: same reference_number and source
  if (opp1.reference_number && opp2.reference_number) {
    const ref1 = normalizeText(opp1.reference_number);
    const ref2 = normalizeText(opp2.reference_number);
    if (ref1 === ref2 && opp1.source === opp2.source) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if two opportunities are similar duplicates
 */
function isSimilarDuplicate<T extends DedupeFields>(opp1: T, opp2: T, similarityThreshold: number = 0.85): boolean {
  // Similar match: title similarity + same organization + deadline proximity
  const titleSimilarity = calculateSimilarity(opp1.title, opp2.title);
  const orgMatch = normalizeText(opp1.organization) === normalizeText(opp2.organization);
  const deadlineProximate = datesAreProximate(opp1.deadline, opp2.deadline, 7);
  
  // Require high title similarity AND organization match AND deadline proximity
  if (titleSimilarity >= similarityThreshold && orgMatch && deadlineProximate) {
    return true;
  }
  
  return false;
}

/**
 * Check if two opportunities have the same URL
 */
function isUrlDuplicate<T extends DedupeFields>(opp1: T, opp2: T): boolean {
  if (!opp1.url || !opp2.url) return false;
  
  const url1 = normalizeUrl(opp1.url);
  const url2 = normalizeUrl(opp2.url);
  
  return url1 === url2;
}

/**
 * Deduplicate opportunities across multiple sources
 * 
 * Priority order:
 * 1. Exact duplicates (reference_number + source) - keep first occurrence
 * 2. URL duplicates - keep first occurrence
 * 3. Similar duplicates (title + organization + deadline) - keep first occurrence
 * 
 * @param opportunities - Array of opportunities to deduplicate
 * @param similarityThreshold - Minimum similarity score for similar matches (0-1)
 * @returns Deduplication result with unique opportunities and duplicate information
 */
export function deduplicateOpportunities<T extends DedupeFields>(
  opportunities: T[],
  similarityThreshold: number = 0.85
): DeduplicationResult<T> {
  const unique: T[] = [];
  const duplicates: DeduplicationResult<T>['duplicates'] = [];
  const seen = new Set<string>();
  
  let exactMatches = 0;
  let similarMatches = 0;
  let urlMatches = 0;
  
  for (const opp of opportunities) {
    let isDuplicate = false;
    let matchedWith: T | null = null;
    let matchType: 'exact' | 'similar' | 'url' | null = null;
    
    // Check against all unique opportunities seen so far
    for (const uniqueOpp of unique) {
      // 1. Check for exact duplicate
      if (isExactDuplicate(opp, uniqueOpp)) {
        isDuplicate = true;
        matchedWith = uniqueOpp;
        matchType = 'exact';
        exactMatches++;
        break;
      }
      
      // 2. Check for URL duplicate
      if (!isDuplicate && isUrlDuplicate(opp, uniqueOpp)) {
        isDuplicate = true;
        matchedWith = uniqueOpp;
        matchType = 'url';
        urlMatches++;
        break;
      }
      
      // 3. Check for similar duplicate
      if (!isDuplicate && isSimilarDuplicate(opp, uniqueOpp, similarityThreshold)) {
        isDuplicate = true;
        matchedWith = uniqueOpp;
        matchType = 'similar';
        const similarity = calculateSimilarity(opp.title, uniqueOpp.title);
        similarMatches++;
        duplicates.push({
          opportunity: opp,
          matchedWith: uniqueOpp,
          matchType: 'similar',
          similarity,
        });
        break;
      }
    }
    
    if (!isDuplicate) {
      unique.push(opp);
      // Create a key for this opportunity to track it
      const key = `${opp.source}-${opp.reference_number || opp.id}`;
      seen.add(key);
    } else if (matchedWith && matchType) {
      // Only add to duplicates if not already added (for similar matches)
      if (matchType !== 'similar') {
        duplicates.push({
          opportunity: opp,
          matchedWith,
          matchType,
        });
      }
    }
  }
  
  return {
    unique,
    duplicates,
    stats: {
      total: opportunities.length,
      unique: unique.length,
      duplicates: duplicates.length,
      exactMatches,
      similarMatches,
      urlMatches,
    },
  };
}

/**
 * Merge duplicate opportunities, keeping the most complete data
 */
export function mergeDuplicateOpportunities(
  opp1: Opportunity,
  opp2: Opportunity
): Opportunity {
  // Prefer the opportunity with more complete data
  const score1 = calculateCompletenessScore(opp1);
  const score2 = calculateCompletenessScore(opp2);
  
  const primary = score1 >= score2 ? opp1 : opp2;
  const secondary = score1 >= score2 ? opp2 : opp1;
  
  // Merge fields, preferring primary but filling gaps from secondary
  return {
    ...primary,
    description: primary.description || secondary.description,
    contact_name: primary.contact_name || secondary.contact_name,
    contact_email: primary.contact_email || secondary.contact_email,
    contact_phone: primary.contact_phone || secondary.contact_phone,
    amount: primary.amount || secondary.amount,
    value: primary.value || secondary.value,
    postedDate: primary.postedDate || secondary.postedDate,
    metadata: {
      ...(primary.metadata || {}),
      ...(secondary.metadata || {}),
      merged_from: [
        ...(primary.metadata?.merged_from || [primary.source]),
        ...(secondary.metadata?.merged_from || [secondary.source]),
      ],
    },
  };
}

/**
 * Calculate completeness score for an opportunity (0-1)
 */
function calculateCompletenessScore(opp: Opportunity): number {
  let score = 0;
  let maxScore = 0;
  
  // Title (required)
  maxScore += 1;
  if (opp.title && opp.title.length > 0) score += 1;
  
  // Description
  maxScore += 1;
  if (opp.description && opp.description.length > 50) score += 1;
  
  // Organization
  maxScore += 1;
  if (opp.organization && opp.organization.length > 0) score += 1;
  
  // Deadline
  maxScore += 1;
  if (opp.deadline) score += 1;
  
  // URL
  maxScore += 1;
  if (opp.url) score += 1;
  
  // Contact info
  maxScore += 1;
  if (opp.contact_email || opp.contact_phone || opp.contact_name) score += 1;
  
  // Amount
  maxScore += 1;
  if (opp.amount || opp.value) score += 1;
  
  // Reference number
  maxScore += 1;
  if (opp.reference_number) score += 1;
  
  return maxScore > 0 ? score / maxScore : 0;
}

/**
 * Minimal shape of the govt_opportunity `SchemaEntity` records rendered by
 * OpportunitiesDashboard (see `GovtOpportunityEntity` there). Kept structural/loose here
 * (rather than importing the full dashboard type) to avoid a dependency from this
 * generic util back onto a page component.
 */
export interface GovtOpportunityEntityLike {
  id: string | number;
  data: {
    title?: string;
    organization?: string;
    deadline?: string;
    closing_date?: string;
    response_deadline?: string;
    url?: string;
    document_url?: string;
    reference_number?: string;
    solicitation_number?: string;
    source?: string;
    source_system?: string;
    data_source?: string;
  };
}

/**
 * Deduplicate govt_opportunity entities (as used by OpportunitiesDashboard's DB/semantic
 * search results) using the same exact/URL/similar-match rules as `deduplicateOpportunities`.
 *
 * The same real-world tender is frequently ingested as separate `SchemaEntity` rows with
 * different ids (re-scraped on a later sync, mirrored across multiple source portals,
 * etc.), so a multi-token keyword search recalls all of them and they render as 2-3x
 * duplicate cards. ID-based de-duping doesn't catch this because the ids are genuinely
 * different; this compares title/organization/deadline/URL/reference-number instead and
 * keeps the first (i.e. highest-ranked) occurrence, preserving the original entity object.
 */
export function deduplicateGovtOpportunityEntities<T extends GovtOpportunityEntityLike>(
  entities: T[],
  similarityThreshold: number = 0.85
): T[] {
  if (entities.length <= 1) return entities;

  const proxies = entities.map((entity) => ({
    id: entity.id,
    title: entity.data.title || '',
    organization: entity.data.organization || '',
    deadline: entity.data.deadline || entity.data.closing_date || entity.data.response_deadline || '',
    url: entity.data.url || entity.data.document_url || '',
    reference_number: entity.data.reference_number || entity.data.solicitation_number || '',
    source: entity.data.source || entity.data.source_system || entity.data.data_source || '',
  }));

  const { unique } = deduplicateOpportunities(proxies, similarityThreshold);
  const uniqueIds = new Set(unique.map((p) => String(p.id)));
  return entities.filter((entity) => uniqueIds.has(String(entity.id)));
}

